import { getLogger } from '@packages/core/logging';

import type { AccountEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';
import { toAccountEntity } from '../helpers';

const logger = getLogger('accounts');

/** Minimum number of characters required before triggering a search. */
const MIN_SEARCH_LENGTH = 3;

/**
 * Parameters for searching accounts at a specific retailer. Used to find potential team
 * members by name or username.
 */
export type SearchAccountsParams = {
    /** The text the user typed into the search field. */
    inputValue: string;

    /** The retailer code to scope the search to. */
    retailerNumber: string;
};

/**
 * Search for accounts at a specific retailer by name or username. Validates minimum input length,
 * calls the accounts API, and returns domain entities. Presentation-layer formatting (e.g. mapping
 * to select options) is the caller's responsibility.
 */
export class SearchAccounts extends StsBackendOperation {
    /**
     * Search for accounts matching the input text at the given retailer. Returns an empty array if
     * the input is shorter than the minimum search length or if the API call fails.
     *
     * @param params - the search text and retailer number
     * @returns matching account entities
     */
    async exec(params: SearchAccountsParams): Promise<AccountEntity[]> {
        const { inputValue, retailerNumber } = params;

        // Require a minimum input length before calling the API
        if (inputValue.length < MIN_SEARCH_LENGTH) {
            return [];
        }
        try {
            // Call the API and map DTOs to entities
            const response = await this.client.getAccountsByRetailerNumber({
                retailerCode: retailerNumber,
                text: inputValue,
            });

            if (!response.success) {
                return [];
            }

            return response.data.map(toAccountEntity);
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Failed to get accounts by retailer number';
            logger.error('Failed to search the accounts by retailer number', { error: e });
            throw new Error(message);
        }
    }
}
